import { cn } from "@/lib/utils";
import type { FolderVariant } from "../../../types";
import React from "react";
import { FolderArchive } from "lucide-react";
import { ChevronButton } from "../../atoms/ChevronButton";
import { FolderIcon } from "../../atoms/FolderIcon";
import { FolderName } from "../../atoms/FolderName";
import { DocumentCountBadge } from "../../atoms/DocumentCountBadge";
import { FolderActionMenu } from "./FolderActionMenu";

interface FolderItemProps {
  /** Folder ID */
  id: string;
  /** Folder name */
  name: string;
  /** Whether folder is expanded */
  isExpanded: boolean;
  /** Whether folder has children */
  hasChildren: boolean;
  /** Number of documents in folder */
  documentCount?: number;
  /** Whether folder name is being edited */
  isEditing: boolean;
  /** Whether folder is selected */
  isSelected?: boolean;
  /** Whether folder is being dragged over */
  isDragOver?: boolean;
  /** Whether folder is a valid destination for the active move. */
  isDropAvailable?: boolean;
  /** Native folder move source state. */
  isDraggable?: boolean;
  isDragging?: boolean;
  onDragStart?: React.DragEventHandler<HTMLDivElement>;
  onDragEnd?: React.DragEventHandler<HTMLDivElement>;
  /** Indentation level for nesting */
  level: number;
  /** Called when folder is selected */
  onSelect: () => void;
  /** Called when expand/collapse is toggled */
  onToggleExpand: () => void;
  /** Called when name edit changes */
  onNameChange?: (value: string) => void;
  /** Called when name edit is confirmed */
  onNameConfirm?: (value: string) => void;
  /** Called when name edit is cancelled */
  onNameCancel?: () => void;
  /** Called when create subfolder is selected */
  onCreateSubfolder?: () => void;
  /** Called when rename is selected */
  onRename?: () => void;
  /** Called when delete is selected */
  onDelete?: () => void;
  /** Called when export is selected */
  onExport?: () => void;
  /** Called when "Organize with AI" is selected (WHA-3654) */
  onOrganize?: () => void;
  /** Called when share is selected */
  onShare?: () => void;
  /** Label for delete action */
  deleteLabel?: string;
  /** Whether this is a container (ZIP/archive) entry */
  isContainer?: boolean;
  /** A special folder's own icon — see FolderIcon. */
  variant?: FolderVariant;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FolderItem - Molecule component
 *
 * Single folder row composed from atoms (chevron + icon + name + count + actions).
 * Handles folder selection, expansion, and inline editing.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <FolderItem
 *   id="folder-1"
 *   name="Documents"
 *   isExpanded={true}
 *   hasChildren={true}
 *   documentCount={5}
 *   isEditing={false}
 *   level={0}
 *   onSelect={handleSelect}
 *   onToggleExpand={handleToggle}
 * />
 * ```
 */
export const FolderItem = React.memo(function FolderItem({
  name,
  isExpanded,
  hasChildren,
  documentCount = 0,
  isEditing,
  isSelected = false,
  isDragOver = false,
  isDropAvailable = false,
  isDraggable = false,
  isDragging = false,
  level,
  onSelect,
  onToggleExpand,
  onNameChange,
  onNameConfirm,
  onNameCancel,
  onCreateSubfolder,
  onRename,
  onDelete,
  onExport,
  onOrganize,
  onShare,
  deleteLabel,
  isContainer = false,
  variant,
  className,
  onDragStart,
  onDragEnd,
}: FolderItemProps) {
  // Handle row click - only select folder (chevron handles expand/collapse)
  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on action menu or editing
    if (isEditing) return;
    onSelect();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isEditing) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        "group flex items-center rounded-md py-1.5",
        "transition-all duration-200 ease-out",
        "cursor-pointer select-none",
        isDraggable && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-50",
        isSelected
          ? "bg-primary/10 text-primary hover:bg-primary/15"
          : "hover:bg-slate-50 hover:shadow-folder-hover",
        isDropAvailable && "bg-blue-50/40 ring-1 ring-inset ring-blue-200",
        isDragOver && "bg-blue-50 shadow-folder-drag ring-2 ring-blue-200",
        className
      )}
      onClick={handleRowClick}
      onKeyDown={handleKeyDown}
      data-drop-available={isDropAvailable || undefined}
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      {/* Left section with indentation */}
      <div
        className="flex min-w-0 flex-1 items-center gap-1.5 pr-2"
        style={{ paddingLeft: `${level * 18 + 8}px` }}
      >
        <ChevronButton isExpanded={isExpanded} hasChildren={hasChildren} onClick={onToggleExpand} />

        {isContainer ? (
          <FolderArchive className="h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
        ) : (
          <FolderIcon isOpen={isExpanded} variant={variant} className="flex-shrink-0" />
        )}

        <div className="min-w-0 flex-1 overflow-hidden">
          <FolderName
            name={name}
            isEditing={isEditing}
            onChange={onNameChange}
            onConfirm={onNameConfirm}
            onCancel={onNameCancel}
          />
        </div>
      </div>

      {/* Right section - aligned to right edge */}
      <div
        className="flex flex-shrink-0 items-center gap-2 pr-2"
        data-actions
        onClick={(e) => e.stopPropagation()} // Prevent row click when clicking actions
      >
        {!isEditing && documentCount > 0 && <DocumentCountBadge count={documentCount} />}

        {!isContainer && (
          <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            <FolderActionMenu
              onCreateSubfolder={onCreateSubfolder}
              onRename={onRename}
              onDelete={onDelete}
              onExport={onExport}
              onOrganize={onOrganize}
              onShare={onShare}
              deleteLabel={deleteLabel}
            />
          </div>
        )}
      </div>
    </div>
  );
});
